import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { useAuth } from '@/api/auth'
import { isApiError } from '@/api/client'
import { useCreateInvitation } from '@/api/queries'
import type { Invitation } from '@/api/types'
import { Button, Checkbox, Modal, TextField, useToast } from '@/components/ui'
import styles from './InviteUsersDialog.module.css'

export function InviteUsersDialog({ onClose }: { onClose: () => void }): ReactElement {
  const { server } = useAuth()
  const canEmail = server?.capabilities?.email === true
  const create = useCreateInvitation()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [sendEmail, setSendEmail] = useState(canEmail)
  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const sending = canEmail && sendEmail

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (create.isPending) return
    create.mutate({ kind: 'email', email: email.trim(), role: isAdmin ? 'admin' : 'member', send_email: sending }, {
      onSuccess: setInvitation,
    })
  }

  const copy = async (): Promise<void> => {
    if (!invitation?.invite_url) return
    try {
      await navigator.clipboard.writeText(invitation.invite_url)
      toast.success('Invitation link copied.')
    } catch {
      toast.error('Could not copy the link. Select it and copy it manually.')
    }
  }

  return <Modal open onClose={() => { if (!create.isPending) onClose() }} title={invitation ? 'Invitation created' : 'Invite users'}
    description="They choose their username and password when they join." size="sm"
    closeOnOverlayClick={false}
    footer={invitation ? <Button variant="primary" onClick={onClose}>Done</Button> : <>
      <Button onClick={onClose} disabled={create.isPending}>Cancel</Button>
      <Button type="submit" form="invite-users-form" variant="primary" loading={create.isPending}>
        {sending ? 'Send invitation' : 'Create invitation link'}
      </Button>
    </>}>
    {invitation ? <div className={styles.stack}>
      <p role="status">{invitation.email_delivery === 'queued'
        ? `The invitation to ${invitation.email ?? email.trim()} is queued for email delivery.`
        : 'Share this invitation link with the person you want to invite.'}</p>
      {invitation.invite_url ? <>
        <TextField label="Invitation link" value={invitation.invite_url} readOnly onFocus={(event) => event.target.select()} />
        <Button onClick={() => void copy()}>Copy link</Button>
        <p className={styles.hint}>This link is shown once. It is available until you close this dialog.</p>
      </> : null}
    </div> : <form id="invite-users-form" className={styles.stack} onSubmit={submit}>
      <TextField label="Email address" type="email" required value={email} autoComplete="off"
        error={isApiError(create.error) ? create.error.fieldErrors.email?.[0] : undefined}
        onChange={(event) => setEmail(event.target.value)} disabled={create.isPending} />
      {canEmail ? <Checkbox label="Send invitation by email" checked={sendEmail}
        onChange={(event) => setSendEmail(event.target.checked)} disabled={create.isPending} />
        : <p className={styles.hint}>Email delivery is not configured. Create a link to share, or configure email in Administration → Notifications.</p>}
      <Checkbox label="Make this person an administrator" checked={isAdmin}
        onChange={(event) => setIsAdmin(event.target.checked)} disabled={create.isPending} />
      {create.error ? <p role="alert">{create.error.message}</p> : null}
    </form>}
  </Modal>
}
